import AddIcon from '@mui/icons-material/Add';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RemoveIcon from '@mui/icons-material/Remove';
import { Alert, Box, CircularProgress, FormControl, FormLabel, IconButton, InputAdornment, MenuItem, Paper, Select, SelectChangeEvent, Stack, Table, TableBody, TableCell, TableContainer, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { MandatoryRequirementCategoryIds, RequirementCategoryMap, RequirementLevelMap } from 'Components/CreateJob/CreateJobConstants';
import { getInvalidCategories, getInvalidCategoriesString } from 'Components/CreateJob/CreateJobHelper';
import { IJobDetailActionItems, IRequirementCategory } from 'Components/CreateJob/CreateJobModel';
import { useNotification } from 'Components/Notification';
import { ShTooltip } from 'Components/SharedComponents/ShTooltip';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { ApiState } from 'shared/SharedModels';
import { ShAlert } from 'shared/SharedStyles/ShFeedback';
import { ShButton, ShGreenBtn } from 'shared/SharedStyles/ShInputs';
import { DefaultAPIErrorMsg, SalaryRegEx } from 'shared/constants';
import { areAllElementsInArrayXInArrayY } from 'shared/functions';
import { createOrUpdateJobRequirements, resetCreateOrUpdateJobRequirements } from 'store/slices/employer/create-job/create-job-details-slice';
import { getRequirements, resetCreateOrUpdateJob, resetGetRequirements, setIsPreviewEnabled, updateAllJobDetails, updateJobDetails, updateJobRequirementsInSlice } from 'store/slices/employer/create-job/create-job-shared-slices';
import { IJobDetails, IRequirement } from 'store/slices/employer/jobs/jobs-slice-model';

// Job details action items stack in table cell.
const JobDetailActionItems = ({ addReq, removeReq, requirement, isMandatoryRow }: IJobDetailActionItems) => {
    return (<>
        <Stack flexDirection='row' justifyContent='flex-end'>
            {/* Hide 'Remove' for 1st record since at least one is mandatory */}
            {!isMandatoryRow && <ShTooltip placement='left-end' title='Remove'>
                <span>
                    <IconButton size='small' color='error' onClick={() => removeReq(requirement)}>
                        <RemoveIcon fontSize='small' />
                    </IconButton>
                </span>
            </ShTooltip>}
            <ShTooltip placement='bottom-end' title='Add another'>
                <span>
                    <IconButton size='small' onClick={() => addReq(requirement)}>
                        <AddIcon fontSize='small' />
                    </IconButton>
                </span>
            </ShTooltip>
        </Stack>
    </>);
};

export const JobDetails = () => {
    const dispatch = useAppDispatch();
    const { jobId } = useParams();
    const { displayNotification } = useNotification();
    const navigate = useNavigate();
    const isRouteEnabled = useOutletContext();
    const { createOrUpdateJobReqsResponse, createOrUpdateJobReqsStatus } = useAppSelector((state) => state.employer.createJobDetails);
    const { jobDetails, getRequirementsStatus, getRequirementsResponse, jobRequirements,
        getRequirementsMetaDataStatus, getRequirementsMetaDataResponse, requirementsMetaData, defaultRequirementLevel
    } = useAppSelector((state) => state.employer.createJobShared);
    const { getJobDetailsStatus } = useAppSelector((state) => state.employer.jobInfo);
    const [_jobDetails, _setJobDetails] = useState<IJobDetails>({
        requirements: [],
        benefits: '',
        keywords: '',
        salary_details: '',
        salary_type_id: 0
    });
    const [isSalaryInValid, setIsSalaryInValid] = useState<boolean>(true);
    const [isNextDisabled, setIsNextDisabled] = useState<boolean>(true);

    // Update job details fetched from API.
    useEffect(() => {
        if (getJobDetailsStatus === 'success' && jobDetails) {
            _setJobDetails({ ...jobDetails });
            const reg = new RegExp(SalaryRegEx);
            setIsSalaryInValid(!reg.test(jobDetails.salary_details));
        }
    }, [getJobDetailsStatus, jobDetails]);

    // Get job details if jobId is present in route params.
    useEffect(() => {
        if (isRouteEnabled && jobId !== undefined && !isNaN(parseInt(jobId)) && jobRequirements?.length === 0) {
            dispatch(getRequirements({ jobId: jobId }));
        }
    }, [dispatch, isRouteEnabled, jobId, jobRequirements?.length]);

    const getDefaultRequirement = useCallback((reqCatId: number): IRequirement => {
        return {
            name: '',
            requirement_category_id: reqCatId,
            requirement_level_id: defaultRequirementLevel,
            // Generate random number as id for job requirement to handle new job requirement additions and deletions.
            reqId: Math.floor((Math.random() * 100000) + 1),
            ui_order: 1
        };
    }, [defaultRequirementLevel]);

    // Update the retrieved requirements. 
    useEffect(() => {
        if (jobRequirements && jobRequirements?.length > 0) {
            /*
                Based on the selected Job role in title page the requirements API will fetch requirements
                configured for that Job. If there are no predefined requirements available for a category,
                an empty record with that category Id and default requirement level id will be created. 
            */
            let _jobRequirements: IRequirement[] = [];
            requirementsMetaData?.categories.forEach(rmd => {
                let jobReqsByCat = jobRequirements.filter(jr => jr.requirement_category_id === rmd.id);
                if (jobReqsByCat?.length) {
                    _jobRequirements = _jobRequirements.concat(jobReqsByCat);
                } else {
                    _jobRequirements.push(getDefaultRequirement(rmd.id));
                }
            });
            _setJobDetails((prevJobDetails) => { return { ...prevJobDetails, requirements: _jobRequirements } });
        } else {
            // Default records if there are no requirements saved.
            const def: IRequirement[] = [];
            requirementsMetaData?.categories.forEach(rmd => {
                const defaultRecord: IRequirement = getDefaultRequirement(rmd.id);
                def.push(defaultRecord);
            });
            _setJobDetails((prevJobDetails) => { return { ...prevJobDetails, requirements: def } });
        }
    }, [getDefaultRequirement, jobRequirements, requirementsMetaData?.categories]);

    const removeReq = (req: IRequirement) => {
        const delIndx = _jobDetails.requirements.findIndex(r => r.reqId === req.reqId);
        let tempReqs = [..._jobDetails.requirements];
        tempReqs.splice(delIndx, 1);
        _setJobDetails({ ..._jobDetails, requirements: tempReqs });
    };

    const addReq = (req: IRequirement) => {
        const addIndx = _jobDetails.requirements.findIndex(r => r.reqId === req.reqId);
        const newReq: IRequirement = {
            name: '',
            requirement_category_id: req.requirement_category_id,
            requirement_level_id: defaultRequirementLevel,
            // Generate random number as id for job requirement to handle new job requirement additions and deletions.
            reqId: Math.floor((Math.random() * 100000) + 1),
            ui_order: addIndx
        }
        let tempReqs = [..._jobDetails.requirements];
        tempReqs.splice(addIndx + 1, 0, newReq);
        _setJobDetails({ ..._jobDetails, requirements: tempReqs });
    };

    const updateReq = (req: IRequirement, e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const tempReqs: IRequirement[] = [];
        _jobDetails.requirements?.forEach(rq => {
            let newReq = { ...rq };
            if (rq.reqId === req.reqId) {
                newReq.name = e.target.value;
            }
            tempReqs.push(newReq);
        });
        _setJobDetails({ ..._jobDetails, requirements: tempReqs });
    };

    const updateReqLvl = (req: IRequirement, e: SelectChangeEvent<number>) => {
        const tempReqs: IRequirement[] = [];
        _jobDetails.requirements?.forEach(rq => {
            let newReq = { ...rq };
            if (rq.reqId === req.reqId) {
                newReq.requirement_level_id = e.target.value as number;
            }
            tempReqs.push(newReq);
        });
        _setJobDetails({ ..._jobDetails, requirements: tempReqs });
    };

    const validateSalary = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | undefined) => {
        const reg = new RegExp(SalaryRegEx);
        setIsSalaryInValid(!reg.test(e?.target.value ?? ''));
        _setJobDetails({ ..._jobDetails, salary_details: e?.target.value ?? '' });
    };

    const cOrUJobReqs = () => {
        let payload = { ..._jobDetails };
        // Remove empty requirement fields if any.
        payload.requirements = _jobDetails.requirements.filter(r => r.name?.trim().length > 0);
        dispatch(createOrUpdateJobRequirements({ jobId: parseInt(jobId ?? ''), payload: payload }));
    };

    // Navigate to next page once Job is created or updated successfully.
    useEffect(() => {
        if (isRouteEnabled && createOrUpdateJobReqsStatus === 'success') {
            displayNotification({
                open: true,
                type: 'success',
                message: createOrUpdateJobReqsResponse
            });
            dispatch(setIsPreviewEnabled(true));
            // Update 'allJobDetails' and 'jobRequirements' in shared state.
            const jD = { ..._jobDetails };
            // 'areRequirementsUpdated' boolean to check if the requirements are updated to update the preview rich text.
            jD.areRequirementsUpdated = !areAllElementsInArrayXInArrayY(_jobDetails.requirements.map(r => r.name),
                jobRequirements.map(r => r.name) ?? []);
            dispatch(updateAllJobDetails(jD));
            dispatch(updateJobDetails(jD));
            dispatch(updateJobRequirementsInSlice(jD.requirements));
            dispatch(resetCreateOrUpdateJobRequirements());
            navigate(`/employer/job/${jobId}/preview`);
        }
    }, [_jobDetails, createOrUpdateJobReqsResponse, createOrUpdateJobReqsStatus, dispatch,
        displayNotification, isRouteEnabled, jobId, jobRequirements, navigate]);

    const showFailureAlerts = (apiState: ApiState, msg: string) => {
        switch (apiState) {
            case 'failed':
                return (<>
                    <Box marginBottom={2} width='100%'>
                        <ShAlert severity="error">{msg ?? DefaultAPIErrorMsg}</ShAlert>
                    </Box>
                </>);
            default:
                break;
        }
    };

    // Disable next button.
    useEffect(() => {
        if (requirementsMetaData) {
            setIsNextDisabled(getInvalidCategories(_jobDetails.requirements, requirementsMetaData)?.length > 0);
        }
    }, [_jobDetails, requirementsMetaData]);

    useEffect(() => {
        if (requirementsMetaData && jobRequirements?.length > 0) {
            dispatch(setIsPreviewEnabled(getInvalidCategories(jobRequirements, requirementsMetaData)?.length === 0));
        }
    }, [dispatch, jobRequirements, requirementsMetaData]);

    // Reset all states on unmount.
    useEffect(() => {
        return () => {
            dispatch(resetGetRequirements());
            dispatch(resetCreateOrUpdateJobRequirements());
            dispatch(resetCreateOrUpdateJob());
        }
    }, [dispatch]);

    const getToolTipText = (name: String) => {
        switch (name) {
            case 'responsibility':
                return `What does a day in the life look like for the person holding this job?
                Describe the key responsibilities, and sell the job to your ideal candidate!`;
            case 'education':
                return `Select the minimum required education for your position. The more strict your requirements, the fewer candidates you'll attract.`;
            case 'qualification':
                return `Qualifications are a simple way to identify who doesn’t meet your minimum requirements. 
                Answered by candidates with Yes/No questions, examples are legal status, languages as well as industry specific accreditation's/designations, 
                experience and software or tools etc.  We've provided common qualifications based on the role.`;
            default:
                return `Select the skills needed for your position. The more strict your requirements, the fewer candidates you'll attract.`;
        }
    };

    const getIsMandatoryRow = (category: IRequirementCategory): boolean => {
        return _jobDetails.requirements?.filter(req => req.requirement_category_id === category.id)?.length === 1
    };

    return (<>
        <Stack className='elements-stack' rowGap={2.5}
            minHeight='350px' justifyContent='center'>
            {((_jobDetails.requirements?.length > 0 && getRequirementsStatus !== 'pending' && jobRequirements?.length > 0 &&
                createOrUpdateJobReqsStatus !== 'pending' && getRequirementsMetaDataStatus !== 'pending' && requirementsMetaData) &&
                getInvalidCategories(_jobDetails.requirements, requirementsMetaData)?.length > 0) &&
                <Alert severity="warning"> Add at least one value in&nbsp;
                    {getInvalidCategoriesString(getInvalidCategories(_jobDetails.requirements, requirementsMetaData)
                        .map(invalidCategory => RequirementCategoryMap[invalidCategory.name]))}
                </Alert>}
            {showFailureAlerts(createOrUpdateJobReqsStatus, createOrUpdateJobReqsResponse)}
            {showFailureAlerts(getRequirementsMetaDataStatus, getRequirementsMetaDataResponse)}
            {showFailureAlerts(getRequirementsStatus, getRequirementsResponse)}
            {/* Requirements  */}
            {(getRequirementsMetaDataStatus === 'pending' || getRequirementsStatus === 'pending') ?
                <CircularProgress /> : <>
                    {requirementsMetaData?.categories?.map(category => (
                        <Stack key={category.id} width='100%'>
                            <Typography variant='body2' component={'span'} fontWeight={600} gutterBottom>
                                <Stack columnGap={1} flexDirection='row' alignItems='center'>
                                    {RequirementCategoryMap[category.name]}
                                    <ShTooltip
                                        title={getToolTipText(category.name)} placement="right"><HelpOutlineIcon fontSize="small" color='disabled' /></ShTooltip></Stack>
                            </Typography>
                            <TableContainer component={Paper} variant='outlined'>
                                <Table size="small" aria-label="a dense table">
                                    <TableBody>
                                        {/* Filter the categories based on the requirement category id. Sort the array while rendering */}
                                        {_jobDetails?.requirements?.filter(req => req.requirement_category_id === category.id)
                                            .sort((a, b) => a.ui_order - b.ui_order).map((r, indx) => (
                                                <TableRow key={r.reqId}>
                                                    {category.name === 'responsibility' ? <TableCell>
                                                        <Stack flexDirection='row' justifyContent='space-between'>
                                                            <TextField variant='standard' id={r.name + '_' + indx} placeholder={indx === 0 ? 'Add at least one Job Duty' :
                                                                'Add Another Job Duty'} value={r.name} onChange={(e) => updateReq(r, e)} fullWidth
                                                                error={MandatoryRequirementCategoryIds.includes(category.id) &&
                                                                    getIsMandatoryRow(category) && !r.name} />
                                                            {/* Filter the categories based on the requirement category id and check for last record. */}
                                                            <JobDetailActionItems addReq={addReq} removeReq={removeReq} requirement={r}
                                                                isMandatoryRow={getIsMandatoryRow(category)} />
                                                        </Stack>
                                                    </TableCell> : <>
                                                        <TableCell>
                                                            <TextField variant='standard' placeholder={indx === 0 ? 'Add at least one ' + RequirementCategoryMap[category.name] :
                                                                'Add Another ' + RequirementCategoryMap[category.name]} value={r.name} onChange={(e) => updateReq(r, e)}
                                                                fullWidth error={MandatoryRequirementCategoryIds.includes(category.id) &&
                                                                    getIsMandatoryRow(category) && !r.name} />
                                                        </TableCell>
                                                        <TableCell className='selection-cell'>
                                                            <Select size='small' variant='standard' value={r.requirement_level_id ?? defaultRequirementLevel}
                                                                onChange={(e) => updateReqLvl(r, e)}>
                                                                {requirementsMetaData?.levels?.map(lvl => (
                                                                    <MenuItem key={lvl.id} value={lvl.id}>{RequirementLevelMap[lvl.name]}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </TableCell>
                                                        <TableCell align='right' className='action-cell'>
                                                            <JobDetailActionItems addReq={addReq} removeReq={removeReq} requirement={r}
                                                                isMandatoryRow={getIsMandatoryRow(category)} />
                                                        </TableCell>
                                                    </>}
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Stack>
                    ))}
                </>}
            <FormControl fullWidth>
                <FormLabel id="benefits">
                    <Typography component={'span'} variant="body2" gutterBottom>
                        <Stack columnGap={1} flexDirection='row' alignItems='center'>Compensation
                            <ShTooltip
                                title="Enter the amount of compensation for the position. Employers who add compensation get more candidates!"
                                placement="right"><HelpOutlineIcon fontSize="small" color='disabled' /></ShTooltip>
                        </Stack></Typography>
                </FormLabel>
                <Stack flexDirection={{ xs: 'column', sm: 'row', md: 'row', lg: 'row' }} justifyContent='space-between'
                    columnGap={1} rowGap={1} width='100%' alignItems='flex-start'>
                    <TextField id='compensation' variant="outlined" size='small'
                        helperText="ex: 25, 45k, 30k - 40k" value={_jobDetails.salary_details ?? ''} onChange={(e) => validateSalary(e)}
                        error={isSalaryInValid && _jobDetails.salary_details?.length > 0} InputProps={{
                            endAdornment: <InputAdornment position='end'>
                                <AttachMoneyIcon />
                            </InputAdornment>
                        }} />

                    <ToggleButtonGroup color="primary" size='small' value={_jobDetails.salary_type_id} exclusive
                        aria-label="Compensation type" onChange={(e, newVal) => _setJobDetails({ ..._jobDetails, salary_type_id: newVal })}>
                        {requirementsMetaData?.salaryTypes?.map(slt => (
                            <ToggleButton key={slt.id} value={slt.id}>
                                <Typography textTransform='none' whiteSpace='nowrap'>{slt.monster_name}</Typography>
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Stack>
            </FormControl>
            <FormControl fullWidth>
                <FormLabel id="benefits">
                    <Typography component={'span'} variant="body2" gutterBottom>
                        <Stack columnGap={1} flexDirection='row' alignItems='center'>Benefits
                            <ShTooltip
                                title="Often overlooked are the benefits that you can offer your employees to sell the job and company. Free coffee and snacks,
                                 discounted gym membership, health/dental plans and many other benefits can greatly impact the quality and quantity of applicants."
                                placement="right"><HelpOutlineIcon fontSize="small" color='disabled' /></ShTooltip>
                        </Stack></Typography>
                </FormLabel>
                <textarea className='multiline-text' id='benefits'
                    placeholder="Enter the job benefits of the position you are going to fill, such as: health plan, dental coverage, free snacks and coffee."
                    value={_jobDetails.benefits ?? ''} onChange={(e) => _setJobDetails({ ..._jobDetails, benefits: e.target.value })} />
            </FormControl>
            <FormControl fullWidth>
                <FormLabel id="keywords">
                    <Typography component={'span'} variant="body2" gutterBottom>
                        <Stack columnGap={1} flexDirection='row' alignItems='center'>Keywords
                            <ShTooltip
                                title="Filter applicants by the keywords you want to see in their resume and investigate who the best fit is! 
                                Enter your own or remove existing ones on your job's Job Details page." placement="right"><HelpOutlineIcon
                                    fontSize="small" color='disabled' /></ShTooltip>
                        </Stack></Typography>
                </FormLabel>
                <TextField id='keywords' variant="outlined" size='small' placeholder='Enter keywords to match'
                    value={_jobDetails.keywords ?? ''} onChange={(e) => _setJobDetails({ ..._jobDetails, keywords: e.target.value })} fullWidth />
            </FormControl>
            <Stack flexDirection='row' justifyContent='space-between' paddingY={1} width='100%'>
                <ShButton variant='contained' size='small' disableElevation
                    onClick={() => navigate(`/employer/job/${jobId}/title`)}>Back</ShButton>
                <ShGreenBtn variant='contained' disableElevation onClick={cOrUJobReqs}
                    disabled={isNextDisabled || (_jobDetails.salary_details?.length > 0 && isSalaryInValid) ||
                        createOrUpdateJobReqsStatus === 'pending'}>
                    {createOrUpdateJobReqsStatus === 'pending' ? 'Saving...' : 'Next'}
                </ShGreenBtn>
            </Stack>
        </Stack>
    </>)
};